import React, { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Link2, Loader2, Share2, X } from 'lucide-react';
import { FinancialPlanData } from '../../types/financialPlan';
import { FinancialPlanShareOwnerInfo } from '../../types/financialPlanShare';
import {
  financialPlanShareClipboardText,
  financialPlanShareUrl,
  getFinancialPlanShareForOwner,
  publishFinancialPlanShare,
  revokeFinancialPlanShare,
} from '../../utils/financialPlanShareService';
import './LgcSharePlanModal.css';

interface LgcSharePlanModalProps {
  planId: string | null;
  planName: string;
  planData: FinancialPlanData;
  onClose: () => void;
  onShareActiveChange?: (active: boolean) => void;
}

interface LgcSharePlanButtonProps {
  onClick: () => void;
}

export const LgcSharePlanButton: React.FC<LgcSharePlanButtonProps> = ({ onClick }) => (
  <button type="button" className="lgc-summary-share-btn" onClick={onClick}>
    <Share2 size={14} aria-hidden />
    Share
  </button>
);

const LgcSharePlanModal: React.FC<LgcSharePlanModalProps> = ({
  planId,
  planName,
  planData,
  onClose,
  onShareActiveChange,
}) => {
  const [share, setShare] = useState<FinancialPlanShareOwnerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadShare = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const existing = await getFinancialPlanShareForOwner(planId);
      setShare(existing);
      onShareActiveChange?.(existing?.enabled === true);
    } catch (err) {
      console.error('getFinancialPlanShareForOwner failed', err);
      setError('Could not load share settings.');
    } finally {
      setLoading(false);
    }
  }, [planId, onShareActiveChange]);

  useEffect(() => {
    void loadShare();
  }, [loadShare]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handlePublish = async (regeneratePin = false) => {
    setPublishing(true);
    setError(null);
    try {
      const next = await publishFinancialPlanShare({
        planId,
        planName,
        planData,
        regeneratePin,
      });
      setShare(next);
      onShareActiveChange?.(true);
    } catch (err) {
      console.error('publishFinancialPlanShare failed', err);
      setError('Could not publish share link. Save your plan and try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleCopy = async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(
        financialPlanShareClipboardText(share.slug, share.accessPin),
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to clipboard.');
    }
  };

  const handleRevoke = async () => {
    if (
      !share ||
      !window.confirm(
        'Revoke this share link? People with the link will no longer be able to view the plan.',
      )
    ) {
      return;
    }
    setRevoking(true);
    setError(null);
    try {
      await revokeFinancialPlanShare(planId);
      setShare(null);
      onShareActiveChange?.(false);
    } catch (err) {
      console.error('revokeFinancialPlanShare failed', err);
      setError('Could not revoke share link.');
    } finally {
      setRevoking(false);
    }
  };

  const shareUrl = share ? financialPlanShareUrl(share.slug) : '';

  return (
    <div className="lgc-timeline-overlay" role="presentation" onClick={onClose}>
      <div
        className="lgc-share-modal"
        role="dialog"
        aria-labelledby="lgc-share-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lgc-share-modal__head">
          <div>
            <h2 id="lgc-share-title" className="lgc-share-modal__title">
              Share plan
            </h2>
            <p className="lgc-share-modal__sub">
              Family and friends get a live, read-only timeline after entering a PIN. Changes you
              make here sync automatically.
            </p>
          </div>
          <button type="button" className="lgc-timeline-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="lgc-share-modal__body">
          {loading ? (
            <p className="lgc-share-modal__status">
              <Loader2 size={16} className="lgc-share-modal__spin" aria-hidden />
              Loading share settings…
            </p>
          ) : (
            <>
              {!planId && (
                <p className="lgc-share-modal__hint">
                  Save this plan first to keep a stable share link when you update it later.
                </p>
              )}

              {share ? (
                <div className="lgc-share-modal__active">
                  <label className="lgc-share-modal__label" htmlFor="lgc-share-url">
                    Share link
                  </label>
                  <div className="lgc-share-modal__link-row">
                    <input
                      id="lgc-share-url"
                      className="lgc-share-modal__link-input"
                      readOnly
                      value={shareUrl}
                      onFocus={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      className="lgc-share-modal__copy-btn"
                      onClick={() => void handleCopy()}
                    >
                      {copied ? <Check size={15} /> : <Copy size={15} />}
                      {copied ? 'Copied' : 'Copy link & PIN'}
                    </button>
                  </div>

                  <div className="lgc-share-modal__pin-block">
                    <span className="lgc-share-modal__label">PIN</span>
                    <span className="lgc-share-modal__pin" aria-label="Share PIN">
                      {share.accessPin}
                    </span>
                    <span className="lgc-share-modal__pin-note">
                      Viewers see updates live — no need to republish when you edit the plan.
                    </span>
                  </div>

                  <div className="lgc-share-modal__actions">
                    <button
                      type="button"
                      className="lgc-share-modal__secondary"
                      disabled={publishing}
                      onClick={() => void handlePublish(true)}
                    >
                      New PIN
                    </button>
                    <button
                      type="button"
                      className="lgc-share-modal__danger"
                      disabled={revoking}
                      onClick={() => void handleRevoke()}
                    >
                      {revoking ? 'Revoking…' : 'Revoke link'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="lgc-share-modal__empty">
                  <p>No active share link yet. Create one to send a PIN-protected live plan view.</p>
                  <button
                    type="button"
                    className="lgc-share-modal__primary"
                    disabled={publishing}
                    onClick={() => void handlePublish(false)}
                  >
                    {publishing ? (
                      <>
                        <Loader2 size={15} className="lgc-share-modal__spin" aria-hidden />
                        Creating…
                      </>
                    ) : (
                      <>
                        <Link2 size={15} aria-hidden />
                        Create share link
                      </>
                    )}
                  </button>
                </div>
              )}

              {error && <p className="lgc-share-modal__error">{error}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LgcSharePlanModal;
